/**
 * Performance & Compliance Center
 * Consolidates KPI Dashboard + Compliance Dashboard + Audit Readiness
 * CHECK Phase — ISO 22301 Clauses 9.1-9.2
 */
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import KPIDashboardPage from "./KPIDashboardPage";
import ComplianceDashboardPage from "./ComplianceDashboardPage";
import AuditReadinessDashboard from "./AuditReadinessDashboard";
import ResilienceHeatmap from "../components/ResilienceHeatmap";
import { BarChart3, Shield, FileCheck, Activity, Sparkles } from "lucide-react";

export default function PerformanceCompliancePage() {
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "kpi";
  const [activeTab, setActiveTab] = useState(initialTab);

  return (
    <div className="space-y-6" data-testid="performance-compliance-page">
      {/* Unified Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50 text-[10px]">CHECK Phase</Badge>
          <Badge variant="outline" className="text-slate-500 border-slate-200 text-[10px]">ISO 22301 Clauses 9.1-9.2</Badge>
        </div>
        <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Performance & Compliance Center
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Consolidated monitoring — PDCA performance, ISO compliance, audit readiness, and §8.2.2 resilience evidence
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="kpi" className="flex items-center gap-1.5 text-xs" data-testid="tab-kpi">
            <BarChart3 className="w-3.5 h-3.5" /> KPI Performance
          </TabsTrigger>
          <TabsTrigger value="compliance" className="flex items-center gap-1.5 text-xs" data-testid="tab-compliance">
            <Shield className="w-3.5 h-3.5" /> ISO Compliance
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-1.5 text-xs" data-testid="tab-audit">
            <FileCheck className="w-3.5 h-3.5" /> Audit Readiness
          </TabsTrigger>
          <TabsTrigger value="heatmap" className="flex items-center gap-1.5 text-xs" data-testid="tab-heatmap">
            <Sparkles className="w-3.5 h-3.5" /> Resilience Heatmap
            <Badge variant="outline" className="ml-1 text-violet-700 border-violet-200 bg-violet-50 text-[9px] px-1 py-0">NEW</Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="kpi" className="mt-4">
          <KPIDashboardPage embedded />
        </TabsContent>

        <TabsContent value="compliance" className="mt-4">
          <ComplianceDashboardPage embedded />
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <AuditReadinessDashboard embedded />
        </TabsContent>

        <TabsContent value="heatmap" className="mt-4">
          <ResilienceHeatmap api={api} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
